// URL scheme
//   /:ws/home | /:ws/inbox | /:ws/my-tasks | /:ws/replies | /:ws/assigned-comments
//   /:ws/l/:listId[/:viewId]   list location
//   /:ws/f/:folderId[/:viewId] folder location
//   /:ws/s/:spaceId[/:viewId]  space location
//   /:ws/everything[/:viewId]  all tasks in the workspace
//   /:ws/chat[/:channelId]     /:ws/chat/activity     /:ws/docs[/:docId[/:pageId]]
//   any route + ?t=<taskId>    task opened in the modal
import { useCallback } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import type { Location } from '@shared/types';

export function locationPath(ws: string, loc: Location, viewId?: string | null) {
  const suffix = viewId ? `/${viewId}` : '';
  switch (loc.type) {
    case 'list':
      return `/${ws}/l/${loc.id}${suffix}`;
    case 'folder':
      return `/${ws}/f/${loc.id}${suffix}`;
    case 'space':
      return `/${ws}/s/${loc.id}${suffix}`;
    default:
      return `/${ws}/everything${suffix}`;
  }
}

export function useWorkspaceId(): string {
  return useParams().ws ?? '';
}

/** Open a task in the modal on top of the current page. */
export function useOpenTask() {
  const [params, setParams] = useSearchParams();
  return useCallback(
    (taskId: string | null, opts: { replace?: boolean } = {}) => {
      const next = new URLSearchParams(params);
      if (taskId) next.set('t', taskId);
      else next.delete('t');
      setParams(next, { replace: opts.replace });
    },
    [params, setParams],
  );
}

export function useGo() {
  const navigate = useNavigate();
  const ws = useWorkspaceId();
  return useCallback(
    (path: string) => {
      navigate(path.startsWith('/') ? path : `/${ws}/${path}`);
    },
    [navigate, ws],
  );
}

export function useCurrentPath() {
  return useLocation().pathname;
}
